const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const getToken = () => localStorage.getItem("inv_token");
export const setToken = (t) => localStorage.setItem("inv_token", t);
export const clearToken = () => localStorage.removeItem("inv_token");

const authHeaders = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const errorMessage = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(errorMessage).filter(Boolean).join("; ");
  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.loc ? `${detail.loc.join(".")}: ${detail.msg}` : detail.msg;
    if (typeof detail.message === "string") return detail.message;
    return Object.values(detail).map(errorMessage).filter(Boolean).join("; ");
  }
  return "";
};

const handleResponse = async (res) => {
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("Session expired");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    const error = new Error(errorMessage(err.detail) || `Request failed: ${res.status}`);
    error.detail = err.detail;
    throw error;
  }
  if (res.status === 204) return null;
  return res.json();
};

export const apiGet = (path, options = {}) =>
  fetch(`${API}${path}`, { headers: authHeaders(), signal: options.signal }).then(handleResponse);

export const apiPost = (path, body) =>
  fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handleResponse);

export const apiPut = (path, body) =>
  fetch(`${API}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(body),
  }).then(handleResponse);

export const apiDelete = (path) =>
  fetch(`${API}${path}`, { method: "DELETE", headers: authHeaders() }).then(handleResponse);

export const apiPostForm = (path, formData) =>
  fetch(`${API}${path}`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  }).then(handleResponse);

// Used for optional, best-effort binary fetches (e.g. an invoice image that
// may not exist yet). Deliberately does NOT clear the session / redirect to
// /login on 401 like the other api* helpers — a transient failure on a
// non-essential fetch should never log the user out of the whole page.
export const apiGetBlob = async (path) => {
  const res = await fetch(`${API}${path}`, { headers: authHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    const error = new Error(errorMessage(err.detail) || `Request failed: ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return res.blob();
};

// GST calculation helper
export const calcLineGST = (item) => {
  // Supplemental charge lines (freight, round off, etc.) store the Amount
  // field as a positive magnitude — the Debit/Credit dropdown decides the
  // sign. A credit reduces the invoice total (e.g. Round Off credit), so it
  // must flow through as a negative base/tax, not a positive one.
  const sign = item.is_landed_cost_line && String(item.charge_direction || "").toLowerCase() === "credit" ? -1 : 1;
  const base = sign * parseFloat(item.invoiced_qty ?? item.received_qty ?? 0) * parseFloat(item.unit_price ?? 0);
  if (item.is_interstate) {
    const igst = base * parseFloat(item.igst_rate ?? 18) / 100;
    return { base: round2(base), igst: round2(igst), cgst: 0, sgst: 0, tax: round2(igst), total: round2(base + igst) };
  }
  const cgst = base * parseFloat(item.cgst_rate ?? 9) / 100;
  const sgst = base * parseFloat(item.sgst_rate ?? 9) / 100;
  return { base: round2(base), igst: 0, cgst: round2(cgst), sgst: round2(sgst), tax: round2(cgst + sgst), total: round2(base + cgst + sgst) };
};

export const round2 = (n) => Math.round(n * 100) / 100;

// Waterfall allocation helper (mirrors backend logic)
export const fmtCurrency = (n, currency = "INR") =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: currency || "INR", maximumFractionDigits: 2 }).format(n || 0);

export const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// Date + time, same DD/MM/YYYY convention as fmtDate, for timestamps (last
// sync, last activity) where the time of day matters too.
export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${fmtDate(iso)}, ${d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
};

// Matches the backend's friendly_sap_error() rewrite (sap_error_messages.py)
// so this specific, actionable rejection gets a distinct callout instead of
// blending into the generic red error text.
export const isPeriodClosedError = (message) => typeof message === "string" && message.includes("fiscal period SAP has already closed");

// Rate shown alongside a withholding-tax code on invoice detail screens.
// "0" is an EXPLICIT 0% classification (distinct from no code at all, which
// renders as "None applied" by the caller) -- see goods_wht_threshold.py.
const WHT_CODE_RATE_LABEL = { "54": "0.1%", "0": "0%", Z02: "1%" };
export const fmtWhtCode = (code) => {
  if (!code) return "None applied";
  const rate = WHT_CODE_RATE_LABEL[code];
  return rate ? `Code ${code} · ${rate}` : `Code ${code}`;
};
